import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'

import { useAuth } from '@features/auth'
import { useErrorInfoModal } from '@features/modal'

import { EMAIL_PATTERN } from '@shared/contants'
import {
  useCheckIsEmailUsedMutation,
  useCustomerProfileFieldsQuery,
  useUpdateCustomerMutation,
} from '@shared/generated'
import BackButton from '@shared/ui/back-button'
import Button from '@shared/ui/button'
import ErrorMessage from '@shared/ui/error-message'
import { FormItem } from '@shared/ui/form'
import Input from '@shared/ui/input'
import InputDate from '@shared/ui/input-date'
import LoaderCircle from '@shared/ui/loader-circle'
import Select from '@shared/ui/select'
import flattenProfileInfo from '@shared/uttils/flattenProfileInfo'

import { ProfileInfoForm } from './info'

import styles from './styles.module.scss'

import PencilIcon from '/icons/pencil.svg'

function SkeletonLoading () {
  return (
    <SkeletonTheme>
      <div>
        <Skeleton
          count={3}
          width={'100%'}
          height={56}
          style={{
            borderRadius: 6,
            marginBottom: 20,
          }}
        />
      </div>
      <div>
        <Skeleton
          count={3}
          width={'100%'}
          height={56}
          style={{
            borderRadius: 6,
            marginBottom: 20,
          }}
        />
      </div>
    </SkeletonTheme>
  )
}

export default function ProfileEdit () {
  const { openErrorInfoModal } = useErrorInfoModal()
  const { t: tv } = useTranslation('validation')
  const { t: tc } = useTranslation('common')
  const { t: ta } = useTranslation('auth')
  const { t: tu } = useTranslation('user')
  const { t: te } = useTranslation('errors')
  const {
    data,
    loading: loadingProfileFields,
    refetch: refetchProfileFields,
  } = useCustomerProfileFieldsQuery()
  const [ fetchCheckEmail, { loading: checkEmailLoading } ] =
    useCheckIsEmailUsedMutation()
  const [ isCorrectEmail, setIsCorrectEmail ] = useState(true)
  const [ originalEmail, setOriginalEmail ] = useState<string | null>(null)
  const [ updateCustomer, { loading: updateCustomerLoading } ] =
    useUpdateCustomerMutation()
  const [ isEdit, setIsEdit ] = useState<boolean>(false)
  const { refetchUser } = useAuth()
  const isTablet = useMediaQuery('(max-width: 991px)')

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ProfileInfoForm>({
    defaultValues: {
      profileInfo: {},
    },
    mode: 'onChange',
  })

  const REQUIRED_MESSAGE = tv('required')
  const EMAIL_MESSAGE = tv('email')

  const onSubmit = async (dto: ProfileInfoForm) => {
    try {
      const answers = flattenProfileInfo(dto.profileInfo).map((i) => ({
        fieldName: i.fieldName,
        id: data?.customerProfileFields?.find(
          (f) => f?.fieldName === i.fieldName,
        )?.id as string,
        value: String(i.value),
      }))

      await updateCustomer({
        variables: {
          answers,
        },
      })

      await refetchProfileFields()

      await refetchUser()

      setIsEdit(false)
    }
    catch (error) {
      openErrorInfoModal({
        description: te('edit_profile'),
      })
      console.log('Customer profile edit error: ', error)
    }
  }

  const emailField = getValues('profileInfo.email')

  const handleCheckEmail = async () => {
    if (!emailField) return

    if (emailField === originalEmail) {
      setIsCorrectEmail(true)
      return
    }

    try {
      const res = await fetchCheckEmail({
        variables: { email: emailField as string },
      })

      setIsCorrectEmail(!res.data?.checkIsEmailUsed)
    }
    catch (_error) {
      setIsCorrectEmail(true)
      openErrorInfoModal({
        description: te('check_email'),
      })
    }
  }

  useEffect(() => {
    if (data?.customerProfileFields) {
      const defaultValues: Record<string, unknown> = {}
      let emailValue = ''

      data.customerProfileFields.forEach((field) => {
        if (field?.fieldName && field?.answer) {
          if (field.fieldName.includes('.')) {
            const keys = field.fieldName.split('.')
            let currentLevel = defaultValues as Record<string, unknown>

            for (let i = 0; i < keys.length; i++) {
              const key = keys[i]

              if (i === keys.length - 1) {
                currentLevel[key] = field.answer
              }
              else {
                if (
                  !currentLevel[key] ||
                  typeof currentLevel[key] !== 'object'
                ) {
                  currentLevel[key] = {}
                }
                currentLevel = currentLevel[key] as Record<string, unknown>
              }
            }
          }
          else {
            defaultValues[field.fieldName] = field.answer
          }

          if (field.type === 'email') {
            emailValue = field.answer
          }
        }
      })

      setOriginalEmail(emailValue)

      const profileInfo = defaultValues as Record<string, string | boolean | null | undefined>
      reset({ profileInfo })
    }
  }, [ data, reset ])

  useEffect(() => {
    if (data) {
      watch()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ data ])

  useEffect(() => {
    if (emailField && !errors.profileInfo?.email?.message) {
      handleCheckEmail()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ emailField ])

  useEffect(() => {
    refetchProfileFields()
  }, [ refetchProfileFields ])

  const formValues = getValues()

  const HAS_CHANGES = useMemo(() => {
    if (!data?.customerProfileFields) return

    for (const { fieldName, value } of flattenProfileInfo(
      formValues.profileInfo,
    )) {
      const isEqual =
        data?.customerProfileFields.find((f) => f?.fieldName == fieldName)
          ?.answer === value

      if (!isEqual) {
        return true
      }
    }

    return false
  }, [ formValues, data ])

  const LOADING = useMemo(() => {
    return loadingProfileFields || updateCustomerLoading
  }, [ loadingProfileFields, updateCustomerLoading ])

  return (
    <div className={styles.edit}>
      <div className={styles.top}>
        {isTablet && <BackButton />}

        <h1 className={styles.edit__title}>{tu('profileInfo.title2')}</h1>
        {!isEdit && (
          <Button
            className={styles.edit__button}
            theme="grey"
            onClick={() => {
              setIsEdit(true)
            }}
          >
            <img src={PencilIcon} alt="" />
          </Button>
        )}
      </div>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.form__grid}>
          {LOADING && <SkeletonLoading />}

          {!LOADING &&
            data?.customerProfileFields?.map((item) => {
              const fieldName = item?.fieldName
              if (!fieldName) return null

              if (item?.type === 'string') {
                return (
                  <FormItem key={item.id}>
                    <Input
                      placeholder={item.label || ''}
                      {...register(`profileInfo.${fieldName}`, {
                        required: item.required ? REQUIRED_MESSAGE : false,
                      })}
                      isInvalid={!!errors?.profileInfo?.[fieldName]?.message}
                      disabled={!isEdit}
                      theme={!isEdit ? 'grey' : undefined}
                      inputSize="extra_large"
                    />
                    {errors.profileInfo?.[fieldName]?.message && (
                      <ErrorMessage>
                        {String(errors.profileInfo[fieldName]?.message)}
                      </ErrorMessage>
                    )}
                  </FormItem>
                )
              }

              if (item?.type === 'email') {
                return (
                  <FormItem key={item.id}>
                    <Input
                      placeholder={item.label || ''}
                      {...register(`profileInfo.${fieldName}`, {
                        required: item.required ? REQUIRED_MESSAGE : false,
                        pattern: {
                          value: EMAIL_PATTERN,
                          message: EMAIL_MESSAGE,
                        },
                      })}
                      endDecorator={
                        checkEmailLoading && (
                          <span className={styles['input-decorator-end']}>
                            <LoaderCircle />
                          </span>
                        )
                      }
                      isInvalid={
                        !!errors?.profileInfo?.[fieldName]?.message ||
                        (!isCorrectEmail && emailField !== originalEmail)
                      }
                      theme={!isEdit ? 'grey' : undefined}
                      disabled={!isEdit}
                      inputSize="extra_large"
                    />
                    {errors.profileInfo?.[fieldName]?.message && (
                      <ErrorMessage>
                        {String(errors.profileInfo[fieldName]?.message)}
                      </ErrorMessage>
                    )}
                    {!isCorrectEmail && emailField !== originalEmail && (
                      <ErrorMessage>{ta('error.email.taken')}</ErrorMessage>
                    )}
                  </FormItem>
                )
              }

              if (item?.type === 'select') {
                const options =
                  item.fixedAnswers?.map((answer) => ({
                    name: answer?.name || '',
                    value: answer?.value || '',
                  })) || []

                return (
                  <FormItem key={fieldName}>
                    <Controller
                      name={`profileInfo.${fieldName}`}
                      control={control}
                      defaultValue=""
                      rules={{
                        required: item.required ? REQUIRED_MESSAGE : false,
                      }}
                      render={({ field }) => (
                        <Select
                          options={options}
                          placeholder={item.label}
                          value={field.value as string}
                          handleOption={field.onChange}
                          theme={!isEdit ? 'grey' : undefined}
                          inputSize="extra_large"
                          disabled={!isEdit}
                        />
                      )}
                    />
                    {errors.profileInfo?.[fieldName]?.message && (
                      <ErrorMessage>
                        {String(errors.profileInfo[fieldName]?.message)}
                      </ErrorMessage>
                    )}
                  </FormItem>
                )
              }

              if (item.type === 'date') {
                return (
                  <FormItem key={fieldName}>
                    <Controller
                      name={`profileInfo.${fieldName}`}
                      control={control}
                      rules={{
                        required: item.required ? REQUIRED_MESSAGE : false,
                      }}
                      render={({ field }) => (
                        <InputDate
                          value={field.value as any}
                          onChange={field.onChange}
                          placeholder={`${item.label}`}
                          theme={!isEdit ? 'grey' : undefined}
                          inputSize="extra_large"
                          disabled={!isEdit}
                        />
                      )}
                    />
                    {errors.profileInfo?.[fieldName]?.message && (
                      <ErrorMessage>
                        {String(errors.profileInfo[fieldName]?.message)}
                      </ErrorMessage>
                    )}
                  </FormItem>
                )
              }

              return null
            })}
        </div>

        {HAS_CHANGES && !loadingProfileFields && (
          <Button
            className={styles.button}
            type="submit"
            size="large"
            disabled={
              checkEmailLoading ||
              (!isCorrectEmail && emailField !== originalEmail)
            }
          >
            {tc('save')}
          </Button>
        )}
      </form>
    </div>
  )
}
